<template>
    <v-dialog v-model="visible" max-width="700" persistent>
        <v-card>
            <!-- Header -->
            <div class="dialog-header d-flex align-center justify-space-between px-6 py-3">
                <span class="dialog-title medium--text">{{ $t('esgPlatform.esgReport.componentSettings') }}</span>
                <v-btn icon @click="close" class="white--text">
                    <v-icon medium>mdi-close</v-icon>
                </v-btn>
            </div>
            <v-card-text class="pa-6">
                <v-row>
                    <v-col cols="12">
                        <div class="setting-label">{{ $t('esgPlatform.esgReport.backgroundColor') }}</div>
                        <v-text-field v-model="localSettings.bgColor" prepend-inner-icon="" class="color-input"
                            hide-details outlined :style="{ '--color-box': localSettings.bgColor }">
                            <template v-slot:prepend-inner>
                                <span class="color-box" :style="{ background: localSettings.bgColor }"></span>
                            </template>
                        </v-text-field>
                    </v-col>
                    <v-col cols="6">
                        <div class="setting-label">{{ $t('esgPlatform.esgReport.fontColor') }}</div>
                        <v-text-field v-model="localSettings.fontColor" class="color-input" hide-details outlined
                            :style="{ '--color-box': localSettings.fontColor }">
                            <template v-slot:prepend-inner>
                                <span class="color-box" :style="{ background: localSettings.fontColor }"></span>
                            </template>
                        </v-text-field>
                    </v-col>
                    <v-col cols="6">
                        <div class="setting-label">{{ $t('esgPlatform.esgReport.font') }}</div>
                        <v-select v-model="localSettings.font" :items="fonts" outlined hide-details />
                    </v-col>
                </v-row>
                <template v-if="scorecardImages">
                    <v-divider class="my-4" />
                    <div class="setting-label" style="font-weight: 600;">{{ $t('esgPlatform.esgReport.scorecardSectionImages') }}</div>
                    <div class="setting-label mb-3" style="font-weight: 400; color: #666; font-size: 0.85rem;">{{ $t('esgPlatform.esgReport.scorecardSectionImagesHint') }}</div>
                    <v-row>
                        <v-col cols="12" md="4" v-for="field in scorecardImageFields" :key="field.key">
                            <div class="setting-label mb-1">{{ $t(field.labelKey) }}</div>
                            <div class="logo-upload-box mb-2" @click="triggerScorecardImageInput(field.key)">
                                <v-icon size="32" color="#bdbdbd">mdi-camera</v-icon>
                                <div class="add-image-btn">{{ $t('esgPlatform.esgReport.addImage') }}</div>
                                <input :ref="'scorecardImageInput_' + field.key" type="file" accept="image/*" class="d-none"
                                    @change="onScorecardImageChange($event, field.key)" />
                            </div>
                            <v-progress-linear v-if="scorecardUploading === field.key" :value="uploadPercentage" height="7" color="primary" class="mb-2" />
                            <v-img v-if="localSettings[field.key]" :src="localSettings[field.key]" max-width="160" max-height="70" contain class="mb-2" />
                        </v-col>
                    </v-row>
                </template>
            </v-card-text>
            <v-divider />
            <v-card-actions class="justify-end px-6 pb-6 pt-4">
                <v-btn outlined color="success" class=" mr-4" @click="close">{{ $t('esgPlatform.esgReport.cancel') }}</v-btn>
                <v-btn color="primary"  @click="save">{{ $t('esgPlatform.esgReport.save') }}</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import S3UploadService from "@/_services/S3UploadService";

export default {
    props: {
        value: Boolean,
        settings: Object,
        scorecardImages: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            visible: false,
            localSettings: {
                bgColor: '#ffffff',
                fontColor: '#101010',
                font: 'Poppins',
                environmentImage: '',
                socialImage: '',
                governanceImage: ''
            },
            fonts: [
                'Poppins', 'Arial', 'Roboto', 'Oswald', 'Georgia', 'Times New Roman', 'Helvetica'
            ],
            scorecardImageFields: [
                { key: 'environmentImage', labelKey: 'esgPlatform.esgReport.environmental' },
                { key: 'socialImage', labelKey: 'esgPlatform.esgReport.social' },
                { key: 'governanceImage', labelKey: 'esgPlatform.esgReport.governance' }
            ],
            scorecardUploading: null,
            uploadPercentage: 0
        }
    },
    watch: {
        value(val) {
            this.visible = val
            if (val) {
                this.localSettings = {
                    ...this.localSettings, // defaults
                    ...this.settings       // overrides
                }
            }
        }
    },
    methods: {
        close() {
            this.$emit('input', false)
        },
        save() {
            this.$emit('save', { ...this.localSettings })
            this.$emit('update:settings', { ...this.localSettings })
            this.close()
        },
        triggerScorecardImageInput(key) {
            const refArr = this.$refs['scorecardImageInput_' + key];
            if (refArr && refArr[0]) refArr[0].click();
        },
        async onScorecardImageChange(e, key) {
            const file = e.target.files[0];
            const folder = "dimitra-public-images";
            const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
            if (!file) return;
            if (!allowedTypes.includes(file.type)) {
                this.$notify({
                    title: this.$t("esgPlatform.esgReport.fileUploadFailed") || "Upload Failed",
                    text: this.$t("esgPlatform.esgReport.unsupportedFileFormat") || "Unsupported file format",
                    type: "error",
                });
                e.target.value = '';
                return;
            }
            try {
                this.scorecardUploading = key;
                const result = await S3UploadService.imageUpload(file, folder, (progress) => {
                    this.uploadPercentage = progress;
                });
                if (result && result.fullPath) {
                    this.localSettings[key] = result.fullPath;
                }
            } catch (err) {
                console.error(err);
                this.$notify({
                    title: this.$t("esgPlatform.esgReport.fileUploadFailed") || "Upload Failed",
                    text: this.$t("esgPlatform.esgReport.errorUploadingFile") || "Error uploading file",
                    type: "error",
                });
            } finally {
                this.scorecardUploading = null;
                this.uploadPercentage = 0;
                e.target.value = '';
            }
        }
    }
}
</script>

<style scoped>
.dialog-header {
    background: #117646;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
}

.dialog-title {
    color: #fff;
    font-weight: 700;
    letter-spacing: 0.5px;
}

.setting-label {
    font-size: 1rem;
    font-weight: 500;
    margin-bottom: 8px;
    color: #222;
}

.color-input {
    font-size: 1rem;
}

.color-box {
    display: inline-block;
    width: 28px;
    height: 28px;
    border-radius: 4px;
    border: 1.5px solid #d1d1d1;
    margin-right: 12px;
    vertical-align: middle;
}

.logo-upload-box {
    border: 2px dashed #bdbdbd;
    border-radius: 8px;
    padding: 16px 0 8px 0;
    text-align: center;
    cursor: pointer;
    color: #888;
    background: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.add-image-btn {
    margin-top: 8px;
    background: #4ecb8f;
    color: #fff;
    border-radius: 4px;
    padding: 6px 16px;
    font-size: 14px;
    box-shadow: 0 2px 4px #0001;
}

.d-none {
    display: none;
}
</style>